import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';
import { printInventoryCount } from './printInventoryCount';

export default {
    name: 'InventoryCounts',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails || !this.canViewInventoryCountsUi()) {
            this.logout();
            return;
        }
        await this.loadInventoryUiContext();
        this.GetInfo();
        this.loadStockings();
        if (this.$route && this.$route.query && this.$route.query.id) {
            this.openDetail({ id: Number(this.$route.query.id) });
        }
    },

    computed: {
        isMinistryUser() {
            return this.hasInventoryAndStockAccessUi();
        },
        showOpeningImportCard() {
            return this.canWarehouseInventoryLinesUi() || this.hasInventoryAndStockAccessUi();
        },
        detailLinesFiltered() {
            if (!this.detail || !this.detail.lines) return [];
            const t = this.detailLinesToolbar;
            let rows = this.detail.lines.slice();
            if (t.cancelled === 'exclude') rows = rows.filter((l) => !l.isCancelled);
            else if (t.cancelled === 'only') rows = rows.filter((l) => l.isCancelled);

            const q = (t.search || '').trim().toLowerCase();
            if (q) {
                rows = rows.filter((line) => {
                    const blob = [
                        line.productName,
                        line.tradeName,
                        line.batchNumber,
                        line.packageType,
                        line.countryOfOrigin,
                        line.manufacturerCountry,
                        line.facilityDepartmentName,
                        line.stockingName,
                        line.note,
                        line.nationalFormularyCode,
                        line.id != null ? String(line.id) : '',
                        line.productsId != null ? String(line.productsId) : ''
                    ]
                        .filter((x) => x != null && String(x).trim() !== '')
                        .join(' ')
                        .toLowerCase();
                    return blob.includes(q);
                });
            }

            const diffVal = (line) => this.detailLineDiff(line);
            if (t.diff === 'surplus') rows = rows.filter((l) => diffVal(l) > 0);
            else if (t.diff === 'shortage') rows = rows.filter((l) => diffVal(l) < 0);
            else if (t.diff === 'zero') rows = rows.filter((l) => diffVal(l) === 0);

            const blank = (v) => v == null || String(v).trim() === '';
            if (t.missing === 'trade') rows = rows.filter((l) => blank(l.tradeName));
            else if (t.missing === 'package') rows = rows.filter((l) => blank(l.packageType));
            else if (t.missing === 'origin') rows = rows.filter((l) => blank(l.countryOfOrigin) && blank(l.manufacturerCountry));
            else if (t.missing === 'batch') rows = rows.filter((l) => blank(l.batchNumber));
            else if (t.missing === 'expiry') rows = rows.filter((l) => !l.expiryDate);

            if (t.expiryFrom) {
                const from = new Date(`${t.expiryFrom}T00:00:00`);
                if (!Number.isNaN(from.getTime())) {
                    rows = rows.filter((l) => {
                        if (!l.expiryDate) return false;
                        const d = new Date(l.expiryDate);
                        return !Number.isNaN(d.getTime()) && d >= from;
                    });
                }
            }
            if (t.expiryTo) {
                const to = new Date(`${t.expiryTo}T23:59:59`);
                if (!Number.isNaN(to.getTime())) {
                    rows = rows.filter((l) => {
                        if (!l.expiryDate) return false;
                        const d = new Date(l.expiryDate);
                        return !Number.isNaN(d.getTime()) && d <= to;
                    });
                }
            }

            const sort = t.sort || 'product';
            rows.sort((a, b) => {
                if (sort === 'lineId') return (a.id || 0) - (b.id || 0);
                if (sort === 'batch')
                    return String(a.batchNumber || '').localeCompare(String(b.batchNumber || ''), 'ar', { sensitivity: 'base' });
                if (sort === 'diffDesc') return diffVal(b) - diffVal(a);
                if (sort === 'diffAsc') return diffVal(a) - diffVal(b);
                return String(a.productName || '').localeCompare(String(b.productName || ''), 'ar', { sensitivity: 'base' });
            });
            return rows;
        },
        detailLinesPaged() {
            const size = Number(this.detailLinesPageSize) || 50;
            const start = (this.detailLinesPage - 1) * size;
            return this.detailLinesFiltered.slice(start, start + size);
        },
        detailLinesFilteredTotal() {
            return this.detailLinesFiltered.length;
        },
        detailLinesTotalPages() {
            const size = Number(this.detailLinesPageSize) || 50;
            return Math.max(1, Math.ceil(this.detailLinesFilteredTotal / size));
        },
        addLinePickedBatchLabel() {
            const idx = Number(this.addLineBatchPickIndex);
            if (idx < 0 || !this.addLineBatchOptions[idx]) return '';
            const b = this.addLineBatchOptions[idx];
            const exp = b.expiryDate ? String(b.expiryDate).slice(0, 10) : 'بدون صلاحية';
            const co = b.countryOfOrigin ? ` — ${b.countryOfOrigin}` : '';
            return `${b.batchNumber || ''} — ${exp}${co}`;
        },
        addLineHasPickedBatch() {
            const i = Number(this.addLineBatchPickIndex);
            return i >= 0 && this.addLineBatchOptions && !!this.addLineBatchOptions[i];
        },
        detailLinesPageFrom() {
            if (!this.detailLinesFilteredTotal) return 0;
            return (this.detailLinesPage - 1) * Number(this.detailLinesPageSize) + 1;
        },
        detailLinesPageTo() {
            if (!this.detailLinesFilteredTotal) return 0;
            return Math.min(this.detailLinesFilteredTotal, this.detailLinesPage * Number(this.detailLinesPageSize));
        },
        detailShowQuickSaveColumn() {
            if (!this.detail || Number(this.detail.status) !== 1) return false;
            if (!this.canWarehouseInventoryLinesUi() && !this.canDepartmentInventoryLinesUi()) return false;
            return (this.detail.lines || []).some((l) => l && l.canEditLine && !l.isCancelled);
        },
        detailLinesTableColspan() {
            let n = 13;
            if (this.detailShowQuickSaveColumn) n++;
            if (this.canWarehouseInventoryLinesUi()) n++;
            return n;
        },
        /** أسطر الصفحة الحالية التي يمكن تحديدها (جلسة مفتوحة وغير ملغاة وصلاحية تعديل/إلغاء). */
        detailLinesSelectableOnPage() {
            return (this.detailLinesPaged || []).filter((l) => this.detailLineRowSelectable(l));
        },
        detailLinesPageSelectAllChecked() {
            const sel = this.detailLinesSelectableOnPage;
            if (!sel.length) return false;
            return sel.every((l) => this.isDetailLineSelected(l));
        },
        detailLineSelectedCount() {
            return (this.detailLineSelectedIds || []).length;
        },
        detailLineBulkBarVisible() {
            return !!(this.detail && Number(this.detail.status) === 1 && this.detailLineSelectedCount > 0);
        },
        detailLineBulkSaveableLines() {
            if (!this.detail || !this.detail.lines) return [];
            const idSet = new Set(this.detailLineSelectedIds);
            return this.detail.lines.filter((l) => idSet.has(l.id) && this.detailLineInputsActive(l));
        },
        detailLineBulkSaveableCount() {
            return this.detailLineBulkSaveableLines.length;
        },
        detailLineBulkCancelableLines() {
            if (!this.detail || !this.detail.lines || !this.canWarehouseInventoryLinesUi()) return [];
            if (Number(this.detail.status) !== 1) return [];
            const idSet = new Set(this.detailLineSelectedIds);
            return this.detail.lines.filter((l) => idSet.has(l.id) && !l.isCancelled);
        },
        detailLineBulkCancelableCount() {
            return this.detailLineBulkCancelableLines.length;
        },
        statsCards() {
            return [
                this.statCard('إجمالي الجردات', this.pages, 'primary', 'ti ti-clipboard-data'),
                this.statCard('جردات مفتوحة', this.countWhere(this.Info, (item) => this.matchStatus(item, [1], ['مفتوح'])), 'warning', 'ti ti-lock-open'),
                this.statCard('مرسلة للمراجعة', this.countWhere(this.Info, (item) => this.matchStatus(item, [2], ['مراجعة'])), 'info', 'ti ti-send'),
                this.statCard('جردات مرحلة', this.countWhere(this.Info, (item) => this.matchStatus(item, [3], ['مرح'])), 'success', 'ti ti-checks')
            ];
        }
    },

    data() {
        return {
            invUi: null,
            pageNo: 1,
            pageSize: 10,
            pages: 0,
            Search: '',
            statusFilter: 0,
            Info: [],
            stockings: [],
            detail: null,
            showSubmitPanel: false,
            showFinalizePanel: false,
            showCreateSessionPanel: false,
            selectedStockingId: '',
            createNote: '',
            submitLines: [],
            submitNote: '',
            finalizeForm: { id: 0, note: '' },
            _productSearchTimer: null,
            _addLineBatchesTimer: null,
            addLineForm: {
                sessionId: null,
                productsId: null,
                batchNumber: '',
                expiryDate: null,
                tradeName: '',
                packageType: '',
                countryOfOrigin: '',
                systemQty: 0,
                actualQty: 0,
                note: ''
            },
            productSearch: '',
            productOptions: [],
            productSearchLoading: false,
            /** أسماء تجارية معروفة للصنف المحدد (من أسطر جرد سابقة). */
            addLineTradeNames: [],
            addLineKnownTradeNormalized: [],
            /** true إذا اختار المستخدم اسماً من القائمة (أو طابق القائمة عند blur). */
            addLineTradeNameFromSuggestion: false,
            addLineBatchOptions: [],
            /** -1 = تشغيلة جديدة؛ غير ذلك فهرس في addLineBatchOptions */
            addLineBatchPickIndex: -1,
            showAddLineModal: false,
            detailLinesToolbar: {
                search: '',
                diff: 'all',
                cancelled: 'exclude',
                missing: '',
                sort: 'product',
                expiryFrom: '',
                expiryTo: ''
            },
            detailLinesPage: 1,
            detailLinesPageSize: 50,
            detailLinesJumpId: null,
            detailLineSavingId: null,
            _detailLinesSearchTimer: null,
            /** وحدات العبوة من النظام (قائمة اختيار في إدخال نتائج الجرد). */
            packageUnitMeasures: [],
            /** بلدان المنشأ النشطة من القاموس. */
            originCountries: [],
            /** معرفات أسطر الجدول المحددة (حفظ/إلغاء جماعي). */
            detailLineSelectedIds: [],
            detailLineBulkSaving: false,
            detailLineBulkCancelling: false
        };
    },

    beforeDestroy() {
        if (this._addLineBatchesTimer) {
            clearTimeout(this._addLineBatchesTimer);
            this._addLineBatchesTimer = null;
        }
        try {
            document.body.classList.remove('modal-open');
            document.body.style.overflow = '';
        } catch (e) {
            /* ignore */
        }
    },

    watch: {
        detailLinesFilteredTotal() {
            this.$nextTick(() => {
                if (this.detailLinesPage > this.detailLinesTotalPages) {
                    this.detailLinesPage = this.detailLinesTotalPages;
                }
            });
        },
        showAddLineModal() {
            this.syncInventoryModalsBodyScroll();
        },
        showSubmitPanel() {
            this.syncInventoryModalsBodyScroll();
        },
        'detailLinesToolbar.search'() {
            this.pruneDetailLineSelectionToFiltered();
        },
        'detailLinesToolbar.cancelled'() {
            this.pruneDetailLineSelectionToFiltered();
        },
        'detailLinesToolbar.diff'() {
            this.pruneDetailLineSelectionToFiltered();
        },
        'detailLinesToolbar.missing'() {
            this.pruneDetailLineSelectionToFiltered();
        },
        'detailLinesToolbar.expiryFrom'() {
            this.pruneDetailLineSelectionToFiltered();
        },
        'detailLinesToolbar.expiryTo'() {
            this.pruneDetailLineSelectionToFiltered();
        },
        'detailLinesToolbar.sort'() {
            this.pruneDetailLineSelectionToFiltered();
        }
    },

    methods: {
        syncInventoryModalsBodyScroll() {
            this.$nextTick(() => {
                const open = this.showAddLineModal || this.showSubmitPanel;
                try {
                    if (open) {
                        document.body.classList.add('modal-open');
                        document.body.style.overflow = 'hidden';
                    } else {
                        document.body.classList.remove('modal-open');
                        document.body.style.overflow = '';
                    }
                } catch (e) {
                    /* ignore */
                }
            });
        },
        resetDetailLinesToolbar() {
            this.detailLinesToolbar = {
                search: '',
                diff: 'all',
                cancelled: 'exclude',
                missing: '',
                sort: 'product',
                expiryFrom: '',
                expiryTo: ''
            };
            this.detailLinesPage = 1;
            this.detailLinesPageSize = 50;
            this.detailLinesJumpId = null;
            this.detailLineSavingId = null;
            this.clearDetailLineSelection();
            if (this._detailLinesSearchTimer) {
                clearTimeout(this._detailLinesSearchTimer);
                this._detailLinesSearchTimer = null;
            }
        },
        onDetailLinesSearchInput() {
            if (this._detailLinesSearchTimer) clearTimeout(this._detailLinesSearchTimer);
            this._detailLinesSearchTimer = setTimeout(() => {
                this.detailLinesPage = 1;
                this.pruneDetailLineSelectionToFiltered();
                this._detailLinesSearchTimer = null;
            }, 280);
        },
        onDetailLinesFiltersChange() {
            this.detailLinesPage = 1;
            this.pruneDetailLineSelectionToFiltered();
        },
        onDetailLinesPageSizeChange() {
            this.detailLinesPage = 1;
            this.pruneDetailLineSelectionToFiltered();
        },
        goDetailLinesPage(p) {
            const next = Math.min(this.detailLinesTotalPages, Math.max(1, p));
            this.detailLinesPage = next;
        },
        detailLinesRowNumber(idx) {
            return (this.detailLinesPage - 1) * Number(this.detailLinesPageSize || 50) + idx + 1;
        },
        detailLineDiff(line) {
            if (!line) return 0;
            const raw = line.actualQty !== '' && line.actualQty != null ? Number(line.actualQty) : Number(line.systemQty || 0);
            const act = Number.isNaN(raw) ? 0 : raw;
            const sys = Number(line.systemQty || 0);
            return act - sys;
        },
        detailLineExpiryText(line) {
            if (!line || !line.expiryDate) return '-';
            try {
                return this.momentTime(line.expiryDate).slice(0, 10);
            } catch {
                return '-';
            }
        },
        detailLineInputsActive(line) {
            return !!(this.detail && Number(this.detail.status) === 1 && line && !line.isCancelled && line.canEditLine);
        },
        /** يمكن تحديد السطر لإجراءات جماعية (حفظ سريع و/أو إلغاء أمين مخزن). */
        detailLineRowSelectable(line) {
            if (!line || !this.detail) return false;
            if (Number(this.detail.status) !== 1 || line.isCancelled) return false;
            if (line.canEditLine) return true;
            return this.canWarehouseInventoryLinesUi();
        },
        isDetailLineSelected(line) {
            return !!(line && line.id != null && this.detailLineSelectedIds.indexOf(line.id) >= 0);
        },
        toggleDetailLineSelection(line) {
            if (!this.detailLineRowSelectable(line) || this.detailLineBulkSaving || this.detailLineBulkCancelling) return;
            const id = line.id;
            const next = this.detailLineSelectedIds.slice();
            const i = next.indexOf(id);
            if (i >= 0) next.splice(i, 1);
            else next.push(id);
            this.detailLineSelectedIds = next;
        },
        toggleDetailLinesSelectAllOnPage() {
            if (this.detailLineBulkSaving || this.detailLineBulkCancelling) return;
            const pageLines = this.detailLinesSelectableOnPage;
            if (!pageLines.length) return;
            const pageIds = new Set(pageLines.map((l) => l.id));
            if (this.detailLinesPageSelectAllChecked) {
                this.detailLineSelectedIds = this.detailLineSelectedIds.filter((id) => !pageIds.has(id));
            } else {
                const merged = new Set(this.detailLineSelectedIds);
                pageLines.forEach((l) => merged.add(l.id));
                this.detailLineSelectedIds = Array.from(merged);
            }
        },
        clearDetailLineSelection() {
            this.detailLineSelectedIds = [];
        },
        pruneDetailLineSelectionToFiltered() {
            const rows = this.detailLinesFiltered || [];
            const allowed = new Set(rows.map((l) => l.id));
            this.detailLineSelectedIds = (this.detailLineSelectedIds || []).filter((id) => allowed.has(id));
        },
        pruneDetailLineSelectionToSessionLines() {
            if (!this.detail || !this.detail.lines) {
                this.detailLineSelectedIds = [];
                return;
            }
            const valid = new Set(this.detail.lines.map((l) => l.id));
            this.detailLineSelectedIds = (this.detailLineSelectedIds || []).filter((id) => valid.has(id));
        },
        clampDetailLineActual(line) {
            let q = Number(line.actualQty);
            if (Number.isNaN(q) || q < 0) q = 0;
            line.actualQty = q;
        },
        scrollToDetailLineById() {
            const id = Number(this.detailLinesJumpId);
            if (!this.detail || !id || Number.isNaN(id)) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'أدخل رقم سطر صالحاً (المعرّف في النظام).');
                return;
            }
            const list = this.detailLinesFiltered;
            const idx = list.findIndex((l) => l.id === id);
            if (idx < 0) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'السطر غير موجود أو مخفي بفلتر الملغاة/البحث.');
                return;
            }
            const size = Number(this.detailLinesPageSize) || 50;
            this.detailLinesPage = Math.floor(idx / size) + 1;
            this.$nextTick(() => {
                const el = document.getElementById(`inventory-detail-line-${id}`);
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            });
        },
        saveDetailLineRow(line) {
            if (!this.detail || !line || !line.canEditLine || this.detailLineBulkSaving || this.detailLineSavingId === line.id) return;
            const actual = Number(line.actualQty != null ? line.actualQty : line.systemQty);
            if (Number.isNaN(actual) || actual < 0) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'كمية غير صالحة.');
                return;
            }
            this.detailLineSavingId = line.id;
            this.$http
                .SaveInventoryLineQuantities({
                    sessionId: this.detail.id,
                    lines: [
                        {
                            lineId: line.id,
                            actualQty: actual,
                            note: null,
                            tradeName: line.tradeName != null ? String(line.tradeName) : '',
                            packageType: line.packageType != null ? String(line.packageType) : '',
                            countryOfOrigin: line.countryOfOrigin != null ? String(line.countryOfOrigin) : ''
                        }
                    ]
                })
                .then(() => {
                    this.detailLineSavingId = null;
                    line.actualQty = actual;
                    line.differenceQty = this.detailLineDiff(line);
                    this.$helper.ShowMessage('success', 'تم', 'تم حفظ السطر.');
                })
                .catch((err) => {
                    this.detailLineSavingId = null;
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },
        bulkSaveDetailLines() {
            if (!this.detail || this.detailLineBulkSaving) return;
            const linesToSave = this.detailLineBulkSaveableLines;
            if (!linesToSave.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'لا توجد أسطر محددة قابلة للحفظ السريع (تعديل الكمية/التجاري/العبوة/المنشأ).');
                return;
            }
            for (let i = 0; i < linesToSave.length; i++) {
                const line = linesToSave[i];
                const actual = Number(line.actualQty != null ? line.actualQty : line.systemQty);
                if (Number.isNaN(actual) || actual < 0) {
                    this.$helper.ShowMessage('warning', 'تنبيه', 'كمية غير صالحة في أحد الأسطر المحددة.');
                    return;
                }
            }
            this.detailLineBulkSaving = true;
            this.$http
                .SaveInventoryLineQuantities({
                    sessionId: this.detail.id,
                    lines: linesToSave.map((line) => ({
                        lineId: line.id,
                        actualQty: Number(line.actualQty != null ? line.actualQty : line.systemQty),
                        note: null,
                        tradeName: line.tradeName != null ? String(line.tradeName) : '',
                        packageType: line.packageType != null ? String(line.packageType) : '',
                        countryOfOrigin: line.countryOfOrigin != null ? String(line.countryOfOrigin) : ''
                    }))
                })
                .then(() => {
                    this.detailLineBulkSaving = false;
                    linesToSave.forEach((line) => {
                        const actual = Number(line.actualQty != null ? line.actualQty : line.systemQty);
                        line.actualQty = actual;
                        line.differenceQty = this.detailLineDiff(line);
                    });
                    this.clearDetailLineSelection();
                    this.$helper.ShowMessage('success', 'تم', `تم حفظ ${linesToSave.length} سطراً.`);
                })
                .catch((err) => {
                    this.detailLineBulkSaving = false;
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },
        bulkCancelDetailLines() {
            if (!this.detail || !this.canWarehouseInventoryLinesUi() || this.detailLineBulkCancelling) return;
            const toCancel = this.detailLineBulkCancelableLines;
            if (!toCancel.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'لا توجد أسطر محددة قابلة للإلغاء.');
                return;
            }
            const n = toCancel.length;
            if (!window.confirm(`تأكيد إلغاء ${n} سطراً من الجرد؟ لا يمكن التراجع من الواجهة.`)) return;
            this.detailLineBulkCancelling = true;
            const run = toCancel.reduce(
                (p, line) => p.then(() => this.$http.CancelInventoryCountLine(line.id)),
                Promise.resolve()
            );
            run
                .then(() => {
                    this.detailLineBulkCancelling = false;
                    this.clearDetailLineSelection();
                    this.$helper.ShowMessage('success', 'تم', `تم إلغاء ${n} سطراً.`);
                    this.openDetail({ id: this.detail.id });
                })
                .catch((err) => {
                    this.detailLineBulkCancelling = false;
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },
        loadInventoryUiContext() {
            return this.$http
                .GetInventoryUiContext()
                .then((res) => {
                    this.invUi = res.data || null;
                })
                .catch(() => {
                    this.invUi = null;
                });
        },
        /** إضافة سطر جرد يدوياً (أمين مخزن) — نافذة منفصلة فوق الصفحة. */
        openAddLineFromDetail() {
            if (!this.canWarehouseInventoryLinesUi() || !this.detail || Number(this.detail.status) !== 1) return;
            this.resetAddLineForm(this.detail.id);
            this.ensurePackageUnitMeasures();
            this.ensureOriginCountries();
            this.showAddLineModal = true;
        },
        resetAddLineForm(sessionId) {
            if (!sessionId) return;
            this.addLineForm = {
                sessionId,
                productsId: null,
                batchNumber: '',
                expiryDate: null,
                tradeName: '',
                packageType: '',
                countryOfOrigin: '',
                systemQty: 0,
                actualQty: 0,
                note: ''
            };
            this.productSearch = '';
            this.productOptions = [];
            this.addLineTradeNames = [];
            this.addLineKnownTradeNormalized = [];
            this.addLineTradeNameFromSuggestion = false;
            this.addLineBatchOptions = [];
            this.addLineBatchPickIndex = -1;
            if (this._addLineBatchesTimer) {
                clearTimeout(this._addLineBatchesTimer);
                this._addLineBatchesTimer = null;
            }
        },
        closeAddLineModal() {
            this.showAddLineModal = false;
        },
        onProductSearchDebounced() {
            if (this._productSearchTimer) clearTimeout(this._productSearchTimer);
            this._productSearchTimer = setTimeout(() => {
                this.searchProductsForAddLine(this.productSearch);
            }, 350);
        },
        normalizeAddLineKey(s) {
            return s != null && String(s).trim() !== '' ? String(s).trim().toLowerCase() : '';
        },
        selectProductForAddLine(p) {
            if (!p || !p.productsId) {
                this.$helper.ShowMessage(
                    'warning',
                    'تنبيه',
                    'لا يوجد صنف نظامي مرتبط بهذا البند في المرفق بعد — يُنشأ عادة عبر القائمة النمطية أو الاستيراد.'
                );
                return;
            }
            this.addLineForm.productsId = p.productsId;
            this.productSearch = p.displayName || `${p.scientificName || ''} (${p.nationalCode || ''})`;
            this.productOptions = [];
            this.addLineTradeNameFromSuggestion = false;
            this.addLineBatchOptions = [];
            this.addLineBatchPickIndex = -1;
            this.addLineForm.tradeName = '';
            this.addLineForm.batchNumber = '';
            this.addLineForm.expiryDate = null;
            this.addLineForm.countryOfOrigin = '';
            this.loadAddLineTradeNames();
            this.scheduleLoadAddLineBatches();
        },
        searchProductsForAddLine(query) {
            const q = query != null ? String(query).trim() : '';
            this.productSearch = q;
            if (!q || q.length < 1) {
                this.productOptions = [];
                return;
            }
            if (!this.addLineForm.sessionId) {
                this.productOptions = [];
                return;
            }
            this.productSearchLoading = true;
            this.$http
                .GetInventoryAddLineProducts(this.addLineForm.sessionId, 1, 40, q)
                .then((res) => {
                    this.productSearchLoading = false;
                    this.productOptions = res.data.info || [];
                })
                .catch(() => {
                    this.productSearchLoading = false;
                    this.productOptions = [];
                });
        },
        loadAddLineTradeNames() {
            if (!this.addLineForm.sessionId || !this.addLineForm.productsId) return;
            this.$http
                .GetInventoryAddLineTradeNames(this.addLineForm.sessionId, this.addLineForm.productsId)
                .then((res) => {
                    this.addLineTradeNames = res.data.info || [];
                    this.addLineKnownTradeNormalized = (this.addLineTradeNames || []).map((t) => this.normalizeAddLineKey(t));
                })
                .catch(() => {
                    this.addLineTradeNames = [];
                    this.addLineKnownTradeNormalized = [];
                });
        },
        scheduleLoadAddLineBatches() {
            if (this._addLineBatchesTimer) clearTimeout(this._addLineBatchesTimer);
            this._addLineBatchesTimer = setTimeout(() => {
                this._addLineBatchesTimer = null;
                this.loadAddLineBatches();
            }, 320);
        },
        loadAddLineBatches() {
            if (!this.addLineForm.sessionId || !this.addLineForm.productsId) return;
            const trade = this.addLineForm.tradeName != null ? String(this.addLineForm.tradeName) : '';
            this.$http
                .GetInventoryAddLineBatches(this.addLineForm.sessionId, this.addLineForm.productsId, trade)
                .then((res) => {
                    this.addLineBatchOptions = res.data.info || [];
                    if (this.addLineBatchPickIndex >= this.addLineBatchOptions.length) {
                        this.addLineBatchPickIndex = -1;
                    }
                })
                .catch(() => {
                    this.addLineBatchOptions = [];
                });
        },
        onAddLineTradeInput() {
            this.addLineTradeNameFromSuggestion = false;
            this.scheduleLoadAddLineBatches();
        },
        onAddLineTradeBlur() {
            const v = this.normalizeAddLineKey(this.addLineForm.tradeName);
            if (v && this.addLineKnownTradeNormalized.indexOf(v) >= 0) {
                this.addLineTradeNameFromSuggestion = true;
            }
            this.scheduleLoadAddLineBatches();
        },
        pickAddLineTradeName(t) {
            this.addLineForm.tradeName = t;
            this.addLineTradeNameFromSuggestion = true;
            this.addLineBatchPickIndex = -1;
            this.scheduleLoadAddLineBatches();
        },
        onAddLineBatchPickChange() {
            const idx = Number(this.addLineBatchPickIndex);
            if (idx < 0 || !this.addLineBatchOptions[idx]) {
                return;
            }
            const b = this.addLineBatchOptions[idx];
            this.addLineForm.batchNumber = b.batchNumber != null ? String(b.batchNumber) : '';
            this.addLineForm.expiryDate = b.expiryDate != null && String(b.expiryDate).trim() !== '' ? String(b.expiryDate).slice(0, 10) : null;
            this.addLineForm.countryOfOrigin = b.countryOfOrigin != null ? String(b.countryOfOrigin) : '';
        },
        findAddLineMatchingBatchOptionIndex() {
            const bn = (this.addLineForm.batchNumber || '').trim();
            if (!bn) return -1;
            const ex = this.addLineForm.expiryDate != null && String(this.addLineForm.expiryDate).trim() !== ''
                ? String(this.addLineForm.expiryDate).slice(0, 10)
                : '';
            for (let i = 0; i < this.addLineBatchOptions.length; i++) {
                const b = this.addLineBatchOptions[i];
                const ob = (b.batchNumber || '').trim();
                const oe = b.expiryDate != null && String(b.expiryDate).trim() !== '' ? String(b.expiryDate).slice(0, 10) : '';
                if (ob.toLowerCase() === bn.toLowerCase() && oe === ex) return i;
            }
            return -1;
        },
        submitAddLine() {
            if (!this.addLineForm.sessionId || !this.addLineForm.productsId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'اختر الصنف من القائمة النمطية.');
                return;
            }
            const tradeTrim = this.addLineForm.tradeName != null ? String(this.addLineForm.tradeName).trim() : '';
            if (!tradeTrim) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'أدخل الاسم التجاري أو اختره من القائمة.');
                return;
            }
            const tradeNorm = this.normalizeAddLineKey(tradeTrim);
            if (tradeNorm && this.addLineKnownTradeNormalized.indexOf(tradeNorm) >= 0 && !this.addLineTradeNameFromSuggestion) {
                this.$helper.ShowMessage(
                    'warning',
                    'تنبيه',
                    'هذا الاسم التجاري موجود مسبقاً — اختره من القائمة، أو أدخل اسماً تجارياً جديداً لا يطابق المخزن.'
                );
                return;
            }
            const matchedBatchIdx = this.findAddLineMatchingBatchOptionIndex();
            const treatsAsExistingBatch = this.addLineBatchPickIndex >= 0 || matchedBatchIdx >= 0;
            if (!treatsAsExistingBatch && String(this.addLineForm.countryOfOrigin || '').trim() === '') {
                this.$helper.ShowMessage('warning', 'تنبيه', 'بلد المنشأ مطلوب عند إدخال تشغيلة جديدة.');
                return;
            }
            this.$blockUI.Start();
            this.$http
                .AddInventoryCountsLegacyLine({
                    sessionId: this.addLineForm.sessionId,
                    productsId: this.addLineForm.productsId,
                    batchNumber: this.addLineForm.batchNumber || null,
                    expiryDate: this.addLineForm.expiryDate || null,
                    tradeName: this.addLineForm.tradeName || null,
                    packageType: this.addLineForm.packageType || null,
                    countryOfOrigin: this.addLineForm.countryOfOrigin || null,
                    systemQty: Number(this.addLineForm.systemQty || 0),
                    actualQty: Number(this.addLineForm.actualQty || 0),
                    note: this.addLineForm.note || null
                })
                .then((res) => {
                    this.$blockUI.Stop();
                    this.$helper.ShowMessage('success', 'تم', (res.data && res.data.message) || 'تمت الإضافة.');
                    const sid = this.addLineForm.sessionId;
                    this.showAddLineModal = false;
                    this.loadInventoryUiContext();
                    if (sid) {
                        this.openDetail({ id: sid });
                    }
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },
        GetInfo(pageNo) {
            this.pageNo = pageNo || 1;
            this.$blockUI.Start();
            this.$http
                .GetInventoryCounts(this.pageNo, this.pageSize, this.Search, this.statusFilter)
                .then((response) => {
                    this.$blockUI.Stop();
                    this.Info = response.data.info || [];
                    this.pages = response.data.count || 0;
                })
                .catch(() => {
                    this.$blockUI.Stop();
                    this.Info = [];
                    this.pages = 0;
                });
        },
        loadStockings() {
            this.$http
                .GetAllStocking()
                .then((response) => {
                    this.stockings = response.data.info || response.data || [];
                })
                .catch(() => {
                    this.stockings = [];
                });
        },
        openCreateDialog() {
            this.selectedStockingId = '';
            this.createNote = '';
            this.showCreateSessionPanel = true;
            this.$nextTick(() => {
                const el = document.getElementById('inventory-create-session-panel');
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            });
        },
        collapseCreateSessionPanel() {
            this.showCreateSessionPanel = false;
        },
        submitCreate() {
            if (this.selectedStockingId === '' || this.selectedStockingId == null) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'اختر المخزن أولاً.');
                return;
            }
            this.$blockUI.Start();
            this.$http
                .CreateInventoryCount({ stockingId: Number(this.selectedStockingId), note: this.createNote || null })
                .then((response) => {
                    this.$blockUI.Stop();
                    this.$helper.ShowMessage('success', 'تم', response.data.message || 'تم فتح الجرد.');
                    this.showCreateSessionPanel = false;
                    this.GetInfo(1);
                    if (response.data.id) {
                        this.openDetail({ id: response.data.id });
                    }
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },
        closeSessionView() {
            this.resetDetailLinesToolbar();
            this.detail = null;
            this.showAddLineModal = false;
            this.showSubmitPanel = false;
            this.showFinalizePanel = false;
            this.submitLines = [];
            this.GetInfo(this.pageNo);
        },
        openDetail(item) {
            if (!item || item.id == null) return;
            const sessionId = Number(item.id);
            const focusAddLine = item.focusAddLine === true;
            if (!sessionId || Number.isNaN(sessionId)) return;
            const prevSessionId = this.detail && this.detail.id != null ? Number(this.detail.id) : null;
            this.showAddLineModal = false;
            this.showSubmitPanel = false;
            this.showFinalizePanel = false;
            this.submitLines = [];
            this.$blockUI.Start();
            this.$http
                .GetInventoryCount(sessionId)
                .then((response) => {
                    this.$blockUI.Stop();
                    this.detail = response.data.info;
                    if (prevSessionId == null || prevSessionId !== sessionId) {
                        this.resetDetailLinesToolbar();
                    } else {
                        this.pruneDetailLineSelectionToSessionLines();
                    }
                    if (this.detail && Number(this.detail.status) === 1) {
                        this.ensurePackageUnitMeasures();
                        this.ensureOriginCountries();
                    }
                    if (
                        focusAddLine &&
                        this.canWarehouseInventoryLinesUi() &&
                        this.detail &&
                        Number(this.detail.status) === 1
                    ) {
                        this.resetAddLineForm(sessionId);
                        this.showAddLineModal = true;
                    }
                    this.$nextTick(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    });
                })
                .catch(() => {
                    this.$blockUI.Stop();
                });
        },
        packageTypeInUnitMeasures(val) {
            const t = (val != null ? String(val) : '').trim();
            if (!t) return false;
            return (this.packageUnitMeasures || []).some((u) => (u.name != null ? String(u.name) : '').trim() === t);
        },
        ensurePackageUnitMeasures() {
            return this.$http
                .GetUnitMeasures()
                .then((res) => {
                    this.packageUnitMeasures = res.data.info || [];
                })
                .catch(() => {
                    this.packageUnitMeasures = [];
                });
        },
        countryOfOriginInList(val) {
            const t = (val != null ? String(val) : '').trim();
            if (!t) return false;
            return (this.originCountries || []).some((c) => (c.name != null ? String(c.name) : '').trim() === t);
        },
        ensureOriginCountries() {
            return this.$http
                .GetAllCountries()
                .then((res) => {
                    this.originCountries = res.data.info || [];
                })
                .catch(() => {
                    this.originCountries = [];
                });
        },
        openSubmit() {
            if (!this.detail || !Array.isArray(this.detail.lines)) return;
            if (!this.canSubmitInventoryCountSessionUi()) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'لا تملك صلاحية إرسال الجرد كاملاً.');
                return;
            }
            this.ensurePackageUnitMeasures();
            this.ensureOriginCountries();
            this.submitNote = '';
            this.submitLines = this.detail.lines
                .filter((line) => !line.isCancelled)
                .map((line) => ({
                    lineId: line.id,
                    productName: line.productName,
                    nationalFormularyCode: line.nationalFormularyCode != null ? String(line.nationalFormularyCode) : '',
                    batchNumber: line.batchNumber != null ? String(line.batchNumber) : '',
                    expiryDate: line.expiryDate ? this.momentTime(line.expiryDate).slice(0, 10) : '',
                    tradeName: line.tradeName != null ? String(line.tradeName) : '',
                    packageType: line.packageType != null ? String(line.packageType) : '',
                    countryOfOrigin: line.countryOfOrigin != null ? String(line.countryOfOrigin) : '',
                    systemQty: Number(line.systemQty || 0),
                    actualQty: Number(line.actualQty != null ? line.actualQty : line.systemQty || 0),
                    note: line.note || ''
                }));
            this.showSubmitPanel = true;
            this.showFinalizePanel = false;
        },
        collapseSubmitPanel() {
            this.showSubmitPanel = false;
        },
        clampActual(line) {
            let q = Number(line.actualQty);
            if (isNaN(q) || q < 0) q = 0;
            line.actualQty = q;
        },
        submitCount() {
            const body = {
                id: this.detail.id,
                note: this.submitNote || null,
                lines: this.submitLines.map((line) => ({
                    lineId: line.lineId,
                    actualQty: Number(line.actualQty),
                    note: line.note || null,
                    batchNumber: line.batchNumber != null ? String(line.batchNumber) : '',
                    expiryDate:
                        line.expiryDate != null && String(line.expiryDate).trim() !== ''
                            ? String(line.expiryDate).slice(0, 10)
                            : '',
                    tradeName: String(line.tradeName ?? ''),
                    packageType: String(line.packageType ?? ''),
                    countryOfOrigin: String(line.countryOfOrigin ?? '')
                }))
            };
            this.$blockUI.Start();
            this.$http
                .SubmitInventoryCount(body)
                .then((response) => {
                    this.$blockUI.Stop();
                    const data = response.data;
                    const msg = typeof data === 'string' ? data : (data && data.message) || 'تم';
                    this.$helper.ShowMessage('success', 'تم', msg);
                    const rawSt =
                        data && typeof data === 'object'
                            ? data.facilityOnboardingStatus ?? data.FacilityOnboardingStatus
                            : null;
                    const st = rawSt != null && rawSt !== '' ? Number(rawSt) : null;
                    if (st != null && !Number.isNaN(st) && this.loginDetails) {
                        this.$set(this.loginDetails, 'facilityOnboardingStatus', st);
                        try {
                            localStorage.setItem(
                                'currentUser-client',
                                this.encrypt(JSON.stringify(this.loginDetails), this.PlatFormPass)
                            );
                        } catch (e) { /* ignore */ }
                    }
                    this.showSubmitPanel = false;
                    if (st === 2) {
                        window.location.href = '/FacilityOnboarding/AwaitingPharmacyReview';
                        return;
                    }
                    this.openDetail({ id: this.detail.id });
                    this.GetInfo(this.pageNo);
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },
        openFinalize() {
            if (!this.detail) return;
            if (!this.canApproveInventoryCountsUi()) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'اعتماد الجرد متاح لمسؤول المرفق أو من تُمنح له صلاحية الاعتماد.');
                return;
            }
            this.finalizeForm = { id: this.detail.id, note: '' };
            this.showFinalizePanel = true;
            this.showSubmitPanel = false;
            this.$nextTick(() => {
                const el = document.getElementById('inventory-finalize-panel');
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            });
        },
        collapseFinalizePanel() {
            this.showFinalizePanel = false;
        },
        submitFinalize() {
            this.$blockUI.Start();
            this.$http
                .FinalizeInventoryCount(this.finalizeForm)
                .then((response) => {
                    this.$blockUI.Stop();
                    this.$helper.ShowMessage('success', 'تم', response.data);
                    this.showFinalizePanel = false;
                    this.closeSessionView();
                    this.loadInventoryUiContext();
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },
        cancelLine(line) {
            if (!line || !line.id) return;
            if (!this.canWarehouseInventoryLinesUi() || this.detailLineBulkCancelling) return;
            this.$http
                .CancelInventoryCountLine(line.id)
                .then(() => {
                    this.$helper.ShowMessage('success', 'تم', 'تم إلغاء السطر.');
                    this.openDetail({ id: this.detail.id });
                })
                .catch((err) => {
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },
        openVoucher(voucher) {
            this.openStockVoucher(voucher);
        },
        goOpeningStockImport() {
            this.$router.push('/OpeningStockImport');
        },
        printDetail() {
            if (!this.detail || !this.detail.canPrint) return;
            this.$http
                .MarkInventoryCountPrinted(this.detail.id)
                .then(() => {
                    const opened = printInventoryCount(this.detail);
                    if (!opened) {
                        this.$helper.ShowMessage('warning', 'تنبيه', 'تعذر فتح نافذة الطباعة.');
                        return;
                    }
                    this.openDetail({ id: this.detail.id });
                })
                .catch((err) => {
                    const msg = err.response && err.response.data ? err.response.data : 'تعذر تسجيل الطباعة.';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        }
    }
};
